'use client';

interface ChatHistory {
  id: string;
  title: string;
  type: "image" | "text";
  isActive?: boolean;
}

interface ChatSidebarProps {
  histories: ChatHistory[];
  onNewChat: () => void;
  onSelectChat: (id: string) => void;
  onToggle: () => void;
  activeId?: string;
  isCollapsed?: boolean;
}

export default function ChatSidebar({
  histories,
  onNewChat,
  onSelectChat,
  onToggle,
  activeId,
  isCollapsed = false
}: ChatSidebarProps) {
  // 收起状态 - 只显示图标
  if (isCollapsed) {
    return (
      <div className="w-16 h-full bg-bg-secondary border-r border-white/8 flex flex-col items-center py-4 transition-all duration-300">
        {/* 展开按钮 */}
        <button
          onClick={onToggle}
          className="w-10 h-10 flex items-center justify-center text-white/50 
                     hover:text-white hover:bg-white/10 rounded-xl transition-all mb-4"
          title="展开侧边栏"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <path d="M9 3v18"/>
          </svg>
        </button>
        
        {/* 新对话按钮 */}
        <button
          onClick={onNewChat}
          className="w-10 h-10 flex items-center justify-center 
                     bg-linear-to-r from-white/8 to-white/4 border border-white/10 rounded-xl
                     hover:bg-white/12 hover:border-white/20 transition-all"
          title="新对话"
        >
          <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        {/* 历史对话图标列表 */}
        <div className="flex-1 overflow-y-auto mt-4 space-y-2 custom-scrollbar">
          {histories.slice(0, 8).map((history) => {
            const isActive = history.isActive || history.id === activeId;
            return (
              <button
                key={history.id}
                onClick={() => onSelectChat(history.id)}
                className={`
                  w-10 h-10 flex items-center justify-center rounded-xl transition-all relative
                  ${isActive
                    ? "bg-white/8 text-white"
                    : "text-white/50 hover:bg-white/4 hover:text-white"
                  }
                `}
                title={history.title}
              >
                {isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-linear-to-b from-primary-purple to-primary-pink rounded-r-full" />
                )}
                <span className="text-base">
                  {history.type === "image" ? "🖼️" : "💬"}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // 展开状态 - 完整显示
  return (
    <div className="w-70 h-full bg-bg-secondary border-r border-white/8 flex flex-col transition-all duration-300">
      {/* 顶部：新对话按钮 + 收起按钮 */}
      <div className="p-4 flex items-center gap-2">
        <button
          onClick={onNewChat}
          className="flex-1 h-11 flex items-center justify-center gap-2.5 
                     bg-linear-to-r from-white/8 to-white/4 border border-white/10 rounded-xl
                     hover:bg-white/12 hover:border-white/20 transition-all duration-200
                     text-[15px] font-medium text-white shadow-xs hover:shadow-glow-purple/20"
        >
          <div className="w-5 h-5 rounded-full border border-white/30 flex items-center justify-center">
            <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          新对话
        </button>
        
        {/* 收起按钮 - 右上角 */}
        <button
          onClick={onToggle}
          className="w-11 h-11 flex items-center justify-center text-white/50 
                     hover:text-white hover:bg-white/10 rounded-xl transition-all shrink-0"
          title="收起侧边栏"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <path d="M9 3v18"/>
          </svg>
        </button>
      </div>

      {/* History List */}
      <div className="flex-1 overflow-y-auto px-3 py-2 custom-scrollbar">
        <div className="text-xs text-white/40 mb-3 px-3 font-medium tracking-wide">历史对话记录</div>
        <div className="space-y-1">
          {histories.map((history) => {
            const isActive = history.isActive || history.id === activeId;
            return (
              <button
                key={history.id}
                onClick={() => onSelectChat(history.id)}
                className={`
                  w-full h-12 px-3 flex items-center gap-3 rounded-xl
                  transition-all duration-200 text-left group relative overflow-hidden
                  ${isActive
                    ? "bg-white/8 text-white font-medium"
                    : "text-white/70 hover:bg-white/4 hover:text-white"
                  }
                `}
              >
                {isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-linear-to-b from-primary-purple to-primary-pink rounded-r-full" />
                )}
                
                <span className={`
                  text-lg transition-transform duration-200
                  ${isActive ? 'scale-110' : 'group-hover:scale-110'}
                `}>
                  {history.type === "image" ? "🖼️" : "💬"}
                </span>
                
                <span className="text-sm truncate flex-1">
                  {history.title}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export const mockHistories: ChatHistory[] = [
  { id: "1", title: "图片修改：生成赛博朋克风格", type: "image", isActive: true },
  { id: "2", title: "文案优化：双十一促销文案", type: "text" },
  { id: "3", title: "产品修图：去除背景杂物", type: "image" },
  { id: "4", title: "小红书爆款标题生成", type: "text" },
  { id: "5", title: "视频脚本：美妆测评", type: "text" },
  { id: "6", title: "Logo设计灵感", type: "image" },
  { id: "7", title: "周报润色", type: "text" },
  { id: "8", title: "用户调研问卷大纲", type: "text" },
];
